import { useState } from "react";
import Modal from "./Modal";
import LedPicker, { type LedPickerValue } from "./LedPicker";
import { usePresets, useOverride } from "../api/queries";
import type { OverrideState } from "../api/types";

const TTL_OPTIONS: Array<{ label: string; seconds: number | null }> = [
  { label: "5 min", seconds: 5 * 60 },
  { label: "30 min", seconds: 30 * 60 },
  { label: "2 h", seconds: 2 * 60 * 60 },
  { label: "8 h", seconds: 8 * 60 * 60 },
  { label: "24 h", seconds: 24 * 60 * 60 },
  { label: "Until cleared", seconds: null },
];

interface Props {
  open: boolean;
  onClose: () => void;
  current: OverrideState | null;
}

export default function OverrideModal({ open, onClose, current }: Props) {
  const presets = usePresets();
  const overrideMut = useOverride();

  const initial: LedPickerValue = current && !current.paused
    ? { color: current.color, luminance: current.luminance, effect: current.effect }
    : { color: "#FFAA00", luminance: 70, effect: "solid" };

  const [value, setValue] = useState<LedPickerValue>(initial);
  const [ttlIdx, setTtlIdx] = useState(1);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);

  async function apply() {
    await overrideMut.mutateAsync({
      color: value.color,
      luminance: value.luminance,
      effect: value.effect,
      ttl_seconds: TTL_OPTIONS[ttlIdx].seconds,
    });
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Override color" size="lg">
      <div className="space-y-6">
        {/* Preset picker — clicking a chip loads its color/lum/effect into the picker. */}
        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            From preset
          </div>
          {presets.isLoading ? (
            <p className="text-sm text-slate-500">Loading presets…</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(presets.data?.presets ?? []).map((p) => {
                const isSelected = selectedPreset === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setSelectedPreset(p.id);
                      setValue({ color: p.color, luminance: p.luminance, effect: p.effect });
                    }}
                    className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm ${
                      isSelected
                        ? "border-brand bg-brand/20 text-brand"
                        : "border-slate-700 bg-slate-800 text-slate-200 hover:border-slate-500"
                    }`}
                  >
                    <span
                      className="inline-block h-4 w-4 rounded border border-slate-700"
                      style={{
                        backgroundColor: p.effect === "off" ? "#000" : p.color,
                        opacity: p.effect === "off" ? 0.3 : p.luminance / 100,
                      }}
                    />
                    <span className="font-medium">{p.name}</span>
                    <span className="font-mono text-xs text-slate-400">{p.color}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Or customize
          </div>
          <LedPicker
            value={value}
            onChange={(v) => {
              setValue(v);
              setSelectedPreset(null);
            }}
          />
        </section>

        <section>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Duration
          </div>
          <div className="flex flex-wrap gap-2">
            {TTL_OPTIONS.map((opt, i) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => setTtlIdx(i)}
                className={`rounded-md border px-3 py-1.5 text-sm ${
                  i === ttlIdx
                    ? "border-brand bg-brand/20 text-brand"
                    : "border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={overrideMut.isPending}
            className="btn-primary"
          >
            {overrideMut.isPending ? "Applying…" : "Apply override"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
